<script lang="ts">
import { useRecaptchaClient } from "~~/composables/useRecaptchaClient";

definePageMeta({
  layout: "default",
});

export default {
  data() {
    return {
      email: "",
      loginRules: this.createLoginRules(),
      showSuccess: false,
      errorMessage: "",
      showInvalidToken: false,
      tokenIsLoading: false,
      isLoading: false,
      recaptcha: null as any,
      helper: useHelper(),
    };
  },
  methods: {
    createLoginRules() {
      return {
        email: [
          (v) => !!v || this.$t("login.errors.email"),
          (v) =>
            this.helper.ruleValidationEmail(v) ||
            this.$t("login.errors.emailInvalid"),
        ],
      };
    },
    async getToken() {
      this.$nextTick(async () => {
        this.showSuccess = false;
        this.errorMessage = "";
        this.showInvalidToken = false;

        const { valid } = await this.$refs.form.validate();
        if (valid) {
          try {
            this.tokenIsLoading = true;

            const captchaToken = await this.recaptcha("create_access_token");

            await useUser()
              .requestForNewToken(this.email, captchaToken)
              .then((p) => {
                this.showSuccess = true;
                this.email = "";
              })
              .catch((err: Error) => {
                if (err.message === "Email") {
                  this.errorMessage = this.$t("login.errors.emailInvalid");
                } else if (
                  err.message === "SendEmailNotification_NoRecipientFound"
                ) {
                  this.errorMessage = this.$t("login.errors.noRecipientFound");
                } else {
                  this.errorMessage = this.$t("login.errors.genericError");
                }
              })
              .finally(() => {
                this.tokenIsLoading = false;
                this.$refs.form.reset();
              });
          } catch (error) {
            this.errorMessage = this.$t("login.errors.genericError");
          }
        }
      });
    },
    async openNewAccount() {
      await navigateTo(useLocalePath()("open") + "/");
    },
  },
  async created() {
    const token = this.$route.query.token as string;
    if (token) {
      this.isLoading = true;
      await useUser()
        .requestForJWTToken(token)
        .catch((err) => {
          this.showInvalidToken = true;
        });
      this.isLoading = false;
    }
  },
  async mounted() {
    this.recaptcha = useRecaptchaClient();
  },
};
</script>

<template>
  <div>
    <v-sheet
      v-if="!isLoading"
      :class="$vuetify.display.smAndUp ? 'small-login-screen' : ''"
      rounded
      elevation="6"
      class="pa-4 text-center mx-auto"
    >
      <v-form ref="form">
        <v-text-field
          v-model="email"
          :label="$t('login.email')"
          :rules="loginRules.email"
          clearable
          outlined
          dense
        ></v-text-field>
        <div class="d-flex flex-column mt-1">
          <v-btn
            class="btn-cim"
            block
            @click="getToken"
            :loading="tokenIsLoading"
          >
            {{ $t("login.btnRequestAccess") }}
          </v-btn>
          <br />
          <div>
            <span class="open-account" @click="openNewAccount()">
              {{ $t("login.openAccount") }}
            </span>
          </div>
          <br />
          <v-alert
            v-if="showSuccess"
            :text="$t('login.successMessage')"
            type="success"
          ></v-alert>
          <v-alert
            v-if="errorMessage"
            :text="errorMessage"
            type="error"
          ></v-alert>
          <v-alert
            v-if="showInvalidToken"
            :text="$t('login.errors.invalidToken')"
            type="error"
          ></v-alert>
          <v-alert
            v-if="
              !tokenIsLoading &&
              !showSuccess &&
              !errorMessage &&
              !showInvalidToken
            "
            :text="$t('login.description')"
          ></v-alert>
        </div>
      </v-form>
    </v-sheet>
  </div>
</template>

<style scoped lang="scss">
.v-sheet {
  margin-top: 100px;
}

.small-login-screen {
  width: 400px !important;
}

.open-account {
  text-decoration: none;
  color: #E05C91;
  cursor: pointer;
}
</style>
